import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import BusinessIcon from '@mui/icons-material/Business';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import BlockIcon from '@mui/icons-material/Block';
import SendIcon from '@mui/icons-material/Send';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import Menu from '@mui/material/Menu';
import Snackbar from '@mui/material/Snackbar';
import SnackbarContent from '@mui/material/SnackbarContent';
import { styled } from '@mui/system';
import { DataGrid } from '@mui/x-data-grid';
import {
    DataGridPremium,
    GridColDef,
    GridRenderCellParams,
    GridSortModel,
    GridValueFormatter,
} from '@mui/x-data-grid-premium';
import { ClockIcon } from '@mui/x-date-pickers';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Individual, Organization } from 'utils';
import { adminGetIndividuals } from '../../../admin.individuals.api';
import { adminGetAllOrganizations } from '../../../admin.organizations.api';
import {
    assignPodToIndividual,
    bulkUpdatePods,
    changePodOrganization,
    createPod,
    CustodyStatus,
    detachPodIndividual,
    getPodHistory,
    getPods,
    GetPodsParams,
    getPodSubCounts,
    markPodReturned,
    markPodShipped,
    PodState,
    requestPodReturn,
    togglePodComp,
    togglePodDecommission,
    bulkExternalInvoice,
    bulkRemoveExternalInvoice,
    transferPodToIndividual,
    updatePod,
} from '../pods.api';
import { CreatePodParams, Pod } from '../pods.types';

const ResponsiveForm = styled('form')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    margin: theme.spacing(-1),
}));

const ResponsiveFormItem = styled(Box)(({ theme }) => {
    const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
    return {
        width: isNarrow ? '100%' : '80%',
        margin: theme.spacing(1),
        marginBottom: '6px',
    };
});

export type CustodyDisplay = {
    label: string;
    colorKey: 'blue' | 'red' | 'gray' | 'default';
};

export const getCustodyDisplay = (pod: Pod): CustodyDisplay => {
    const backendLabel = pod.custody_display_status;
    const backendColor = pod.custody_display_color as 'blue' | 'red' | 'gray' | undefined;

    if (backendLabel && backendColor) {
        return { label: backendLabel, colorKey: backendColor };
    }

    const status = pod.custody_status;
    const hasActiveSub = !!pod.has_active_subscription;

    if (status === 'shipped' || status === 'delivered') {
        const base = status === 'shipped' ? 'Shipped' : 'Delivered';
        if (hasActiveSub) {
            return { label: base, colorKey: 'blue' };
        }
        return { label: `${base} - No Sub`, colorKey: 'red' };
    }

    switch (status) {
        case 'in_warehouse':
            return { label: 'In Warehouse', colorKey: 'gray' };
        case 'return_requested':
            return { label: 'Return Requested', colorKey: 'blue' };
        case 'returned':
            return { label: 'Returned', colorKey: 'gray' };
        case 'returning':
            return { label: 'Returning', colorKey: 'blue' };
        default:
            return { label: status || '', colorKey: 'gray' };
    }
};

const PodList = () => {
    const theme = useTheme();
    const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [allOrganizations, setAllOrganizations] = useState<Organization[]>([]);
    const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
    const [pods, setPods] = useState<Pod[]>([]);
    const [individualsForPodCreate, setIndividualsForPodCreate] = useState<Individual[]>([]);
    const [individualsForPodSearch, setIndividualsForPodSearch] = useState<Individual[]>([]);
    const [selectedIndividualForPodCreate, setSelectedIndividualForPodCreate] =
        useState<Individual | null>(null);
    const [selectedIndividualForPodSearch, setSelectedIndividualForPodSearch] =
        useState<Individual | null>(null);
    const [podTransferNameFilterForCreate, setPodTransferNameFilterForCreate] =
        useState<string>('');
    const [podAthleteNameFilterForSearch, setPodAthleteNameFilterForSearch] = useState<string>('');

    const [searchParams, setSearchParams] = useState<GetPodsParams>({
        uuid: '',
        serial_number: '',
        club_id: '',
        simple_id: '',
        organization_uuid: '',
        pod_state: 'all',
        custody_status: 'all',
        athlete_user_uuid: '',
        created_at: '',
        updated_at: '',
        sort: '-created_at',
    });

    // Form data for create new pod form.
    const [newPodData, setNewPodData] = useState({
        organization_uuid: '',
        serial_number: '',
        simple_id: '',
        club_id: '',
        individual_uuid: '',
        decrement_org_pod_subscription: false,
    });

    const [openDialog, setOpenDialog] = useState(false);
    const [dialogTitle, setDialogTitle] = useState('');
    const [dialogMessage, setDialogMessage] = useState('');
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 25,
    });
    const [totalRows, setTotalRows] = useState(0);
    const searchParamsRef = useRef(searchParams);
    const [editedPods, setEditedPods] = useState<{ [key: string]: boolean }>({});
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [editedFields, setEditedFields] = useState<{
        [key: string]: { simple_id?: string; club_id?: string };
    }>({});
    const [bulkSelectedPods, setBulkSelectedPods] = useState<string[]>([]);
    const [headerCheckboxChecked, setHeaderCheckboxChecked] = useState(false);
    const [bulkActionModalOpen, setBulkActionModalOpen] = useState(false);
    const [activeAthleteSubCount, setActiveAthleteSubCount] = useState<number | null>(null);
    const [activeOrgSubCount, setActiveOrgSubCount] = useState<number | null>(null);
    const [partialOrgSubCount, setPartialOrgSubCount] = useState<number | null>(null);
    const [pastDueCount, setPastDueCount] = useState<number | null>(null);
    const [pastDueInUseCount, setPastDueInUseCount] = useState<number | null>(null);
    const [compCount, setCompCount] = useState<number | null>(null);
    const [inStockCount, setInStockCount] = useState<number | null>(null);
    const [inStockDecommissionedCount, setInStockDecommissionedCount] = useState<number | null>(
        null,
    );
    const [activeSubFilter, setActiveSubFilter] = useState<
        | 'activeAthlete'
        | 'activeOrg'
        | 'partialOrg'
        | 'pastDue'
        | 'pastDueInUse'
        | 'comp'
        | 'inStock'
        | 'inStockDecommissioned'
        | null
    >(null);

    useEffect(() => {
        searchParamsRef.current = searchParams;
    }, [searchParams]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const refreshCounts = useCallback(() => {
        getPodSubCounts()
            .then((counts) => {
                if (counts.activeAthleteSub !== null) {
                    setActiveAthleteSubCount(counts.activeAthleteSub);
                }
                if (counts.activeOrgSub !== null) setActiveOrgSubCount(counts.activeOrgSub);
                if (counts.partialOrgSub !== null) setPartialOrgSubCount(counts.partialOrgSub);
                if (counts.pastDue !== null) setPastDueCount(counts.pastDue);
                if (counts.pastDueInUse !== null) setPastDueInUseCount(counts.pastDueInUse);
                if (counts.comp !== null) setCompCount(counts.comp);
                if (counts.inStock !== null) setInStockCount(counts.inStock);
                if (counts.inStockDecommissioned !== null) {
                    setInStockDecommissionedCount(counts.inStockDecommissioned);
                }
            })
            .catch((error) => {
                console.error('Failed to fetch pod subscription counts', error);
            });
    }, []);

    useEffect(() => {
        refreshCounts();
    }, [refreshCounts]);

    useEffect(() => {
        const params: GetPodsParams = {
            ...searchParamsRef.current,
            per_page: paginationModel.pageSize,
            page: paginationModel.page + 1,
        };
        if (activeSubFilter === 'activeAthlete') {
            params.has_active_sub = true;
        } else if (activeSubFilter === 'activeOrg') {
            params.active_org_sub = true;
        } else if (activeSubFilter === 'partialOrg') {
            params.partial_org_sub = true;
        } else if (activeSubFilter === 'pastDue') {
            params.past_due = true;
        } else if (activeSubFilter === 'pastDueInUse') {
            params.past_due_in_use = true;
        } else if (activeSubFilter === 'comp') {
            params.is_comp = true;
        } else if (activeSubFilter === 'inStock') {
            params.in_stock = true;
        } else if (activeSubFilter === 'inStockDecommissioned') {
            params.in_stock_decommissioned = true;
        }
        getPods(params)
            .then((response) => {
                setPods(response.data.data);
                setTotalRows(response.data.meta.total);
            })
            .catch((error) => {
                console.error('Failed to fetch pods', error);
            });
    }, [paginationModel, activeSubFilter]);

    // Handle individual checkbox selection
    const handleCheckboxChange = (podId: any) => {
        setBulkSelectedPods((prev: any) => {
            if (prev.includes(podId)) {
                return prev.filter((id: any) => id !== podId);
            } else {
                return [...prev, podId];
            }
        });
    };

    // Handle header checkbox selection
    const handleHeaderCheckboxChange = () => {
        setHeaderCheckboxChecked(!headerCheckboxChecked);
        if (!headerCheckboxChecked) {
            // Select all visible pods
            const visiblePodIds = pods.map((pod: Pod) => pod.uuid);
            setBulkSelectedPods(visiblePodIds);
        } else {
            // Deselect all pods
            setBulkSelectedPods([]);
        }
    };

    // Handle bulk action modal
    const handleBulkActionClick = () => {
        setBulkActionModalOpen(true);
    };

    const handleCloseBulkActionModal = () => {
        setBulkActionModalOpen(false);
    };

    const openDialogModal = (title: string, message: string) => {
        setDialogTitle(title);
        setDialogMessage(message);
        setOpenDialog(true);
    };

    const closeDialogModal = () => {
        setOpenDialog(false);
    };

    const fetchAllOrganizations = debounce((searchTerm: string, perPage?: number) => {
        // Replace this with your actual API call
        adminGetAllOrganizations(searchTerm, perPage)
            .then((response) => {
                setAllOrganizations(response.data.data);
            })
            .catch((error) => {
                console.error('Failed to fetch organizations', error);
            });
    }, 300);

    useEffect(() => {
        const fetchIndividualsForOrganization = (organizationUuid: string) => {
            adminGetIndividuals({
                'filter[organization]': organizationUuid,
                'filter[name]': podTransferNameFilterForCreate,
                'filter[has_managing_user]': 1,
            })
                .then((response) => {
                    setIndividualsForPodCreate(response.data.data);
                })
                .catch((error) => {
                    console.error('Failed to fetch individuals', error);
                });
        };

        const fetchIndividualsForPodSearch = () => {
            adminGetIndividuals({
                'filter[organization]': searchParams.organization_uuid,
                'filter[name]': podAthleteNameFilterForSearch,
                'filter[has_managing_user]': 1,
            })
                .then((response) => {
                    setIndividualsForPodSearch(response.data.data);
                })
                .catch((error) => {
                    console.error('Failed to fetch individuals', error);
                });
        };

        if (selectedOrganization) {
            fetchIndividualsForOrganization(selectedOrganization.uuid);
        } else {
            setIndividualsForPodCreate([]);
        }

        if (searchParams.organization_uuid || podAthleteNameFilterForSearch) {
            fetchIndividualsForPodSearch();
        } else {
            setIndividualsForPodSearch([]);
        }
    }, [
        selectedOrganization,
        podTransferNameFilterForCreate,
        podAthleteNameFilterForSearch,
        searchParams.organization_uuid,
    ]);

    const fetchOrganizations = debounce((searchTerm: string, perPage?: number) => {
        adminGetAllOrganizations(searchTerm, perPage)
            .then((response) => {
                setOrganizations(response.data.data);
            })
            .catch((error) => {
                console.error('Failed to fetch organizations', error);
            });
    }, 300);

    useEffect(() => {
        fetchAllOrganizations('', 500);
        fetchOrganizations('', 500);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Function to handle form submission
    const handleNewPodSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        // Validate the organization UUID
        if (!newPodData.organization_uuid) {
            openDialogModal('Error', 'Please select an organization.');
            return;
        }

        // Validate the serial number
        const serialNumberRegex = /^[A-F0-9]{16}$/;
        if (!serialNumberRegex.test(newPodData.serial_number)) {
            openDialogModal(
                'Error',
                'Invalid serial number. It must be 16 hexadecimal characters long.',
            );
            return;
        }

        // Validate the organization UUID
        if (!newPodData.simple_id) {
            openDialogModal('Error', 'Please input a simple ID');
            return;
        }

        try {
            const podData: CreatePodParams = {
                organization_uuid: newPodData.organization_uuid,
                serial_number: newPodData.serial_number,
                simple_id: newPodData.simple_id,
                club_id: newPodData.club_id,
                individual_uuid: newPodData.individual_uuid || null,
                decrement_org_pod_subscription: newPodData.decrement_org_pod_subscription,
            };
            await createPod(podData);
            // Clear the form
            setNewPodData({
                organization_uuid: '',
                serial_number: '',
                simple_id: '',
                club_id: '',
                individual_uuid: '',
                decrement_org_pod_subscription: false,
            });
            // Fetch the updated list of pods
            const response = await getPods(searchParams);
            setPods(response.data.data);
            setTotalRows(response.data.meta.total);
            setSelectedIndividualForPodCreate(null);
            openDialogModal(
                'Success',
                `Pod with serial number ${podData.serial_number} created successfully.`,
            );
        } catch (error: any) {
            openDialogModal('Error', error.response.data.message);
            console.error(error);
        }
    };

    const handleFieldChange = useCallback(
        (value: string, row: Pod, field: 'simple_id' | 'club_id') => {
            setEditedFields((prev) => ({
                ...prev,
                [row.uuid]: { ...prev[row.uuid], [field]: value },
            }));
            setEditedPods((prev) => ({ ...prev, [row.uuid]: true }));
        },
        [],
    );

    const handleUpdate = useCallback(
        async (row: Pod) => {
            try {
                const editedData = editedFields[row.uuid] || {};
                const updateData = {
                    simple_id: editedData.simple_id ?? row.simple_id,
                    club_id: editedData.club_id ?? row.club_id,
                };

                await updatePod(row.uuid, updateData);

                setPods((prevPods) =>
                    prevPods.map((pod) =>
                        pod.uuid === row.uuid ? { ...pod, ...updateData } : pod,
                    ),
                );
                setEditedFields((prev) => {
                    const newState = { ...prev };
                    delete newState[row.uuid];
                    return newState;
                });
                setEditedPods((prev) => ({ ...prev, [row.uuid]: false }));
                setSnackbarMessage('Pod updated successfully!');
                setSnackbarOpen(true);
            } catch (error: any) {
                console.error(`Failed to update pod`, error);
                openDialogModal('Error', `Failed to update pod: ${error.response.data.message}`);
            }
        },
        [editedFields],
    );

    const columns: GridColDef[] = [
        {
            field: 'serial_number',
            headerName: 'Serial #',
            width: 170,
            sortable: true,
            renderCell: (params: GridRenderCellParams<Pod>) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span
                        style={{
                            fontSize: '0.75rem',
                            ...(params.row.decommissioned_at
                                ? {
                                      textDecoration: 'line-through',
                                      color: '#999',
                                  }
                                : {}),
                        }}
                    >
                        {params.value}
                    </span>
                    <ContentCopyIcon
                        sx={{
                            fontSize: 14,
                            cursor: 'pointer',
                            color: 'grey.500',
                            '&:hover': { color: 'grey.800' },
                        }}
                        onClick={() => {
                            navigator.clipboard.writeText(params.value ?? '');
                            setSnackbarMessage('Serial number copied!');
                            setSnackbarOpen(true);
                        }}
                    />
                </Box>
            ),
        },
        {
            field: 'simple_id',
            headerName: 'Simple ID',
            minWidth: 175,
            sortable: true,
            renderCell: (params: GridRenderCellParams<Pod>) => (
                <TextField
                    value={editedFields[params.row.uuid]?.simple_id ?? params.value}
                    onChange={(e) => handleFieldChange(e.target.value, params.row, 'simple_id')}
                    size="small"
                    fullWidth
                    sx={{ height: '80%' }}
                    InputProps={{
                        sx: {
                            height: '80%',
                            fontSize: '0.7rem',
                            marginTop: '0.5rem',
                            backgroundColor: 'white',
                        },
                    }}
                />
            ),
        },
        {
            field: 'pod_type',
            headerName: 'Type',
            width: 100,
            sortable: false,
            renderCell: (params: GridRenderCellParams<Pod>) => {
                const isAthlete = params.row.pod_type === 'athlete';
                return (
                    <Chip
                        icon={
                            isAthlete ? (
                                <PersonOutlineIcon
                                    sx={{ fontSize: 14, color: 'white !important' }}
                                />
                            ) : (
                                <BusinessIcon sx={{ fontSize: 14, color: 'white !important' }} />
                            )
                        }
                        label={isAthlete ? 'Athlete' : 'Org'}
                        sx={{
                            borderRadius: '4px',
                            fontSize: '0.6rem',
                            height: '20px',
                            backgroundColor: isAthlete
                                ? theme.palette.info.main
                                : theme.palette.secondary.main,
                            color: 'white',
                            fontWeight: 600,
                            '& span': { padding: '0 4px' },
                            '& .MuiChip-icon': { marginLeft: '4px', marginRight: '-2px' },
                        }}
                    />
                );
            },
        },
        {
            field: 'is_comp',
            headerName: 'Comp',
            width: 70,
            sortable: false,
            renderCell: (params: GridRenderCellParams<Pod>) => {
                if (!params.row.is_comp) return '';
                return (
                    <Chip
                        icon={<CardGiftcardIcon sx={{ fontSize: 14, color: 'white !important' }} />}
                        label="Yes"
                        sx={{
                            borderRadius: '4px',
                            fontSize: '0.6rem',
                            height: '20px',
                            backgroundColor: theme.palette.warning.main,
                            color: 'white',
                            fontWeight: 600,
                            '& span': { padding: '0 4px' },
                            '& .MuiChip-icon': { marginLeft: '4px', marginRight: '-2px' },
                        }}
                    />
                );
            },
        },
        {
            field: 'external_invoice_id',
            headerName: 'Ext Invoice',
            width: 130,
            sortable: false,
            renderCell: (params: GridRenderCellParams<Pod>) => {
                const invoiceId = params.row.external_invoice_id;
                const endDate = params.row.external_invoice_end_date;
                if (!invoiceId) return '';
                const stripeUrl = invoiceId.startsWith('in_')
                    ? `https://dashboard.stripe.com/invoices/${invoiceId}`
                    : `https://dashboard.stripe.com/search?query=${invoiceId}`;
                const formattedDate = endDate ? new Date(endDate).toLocaleDateString('en-CA') : '';
                return (
                    <Tooltip
                        title={`${invoiceId}${
                            params.row.external_invoice_notes
                                ? ` — ${params.row.external_invoice_notes}`
                                : ''
                        }`}
                    >
                        <Box
                            component="a"
                            href={stripeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                height: '100%',
                                gap: 0.5,
                                textDecoration: 'none',
                                color: 'inherit',
                            }}
                        >
                            <ReceiptLongIcon
                                sx={{ fontSize: 16, color: theme.palette.success.main }}
                            />
                            <Typography sx={{ fontSize: '0.7rem' }}>{formattedDate}</Typography>
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'organization_name',
            headerName: 'Organization',
            width: 150,
            sortable: true,
        },
        { field: 'athlete_name', headerName: 'Athlete', width: 150, sortable: true },
        {
            field: 'transfer_into',
            headerName: 'Transfer Request Sent',
            width: 200,
            sortable: false,
            renderCell: (params: GridRenderCellParams<Pod>) => {
                const transferTo = params.row.transfer_request_sent_to;
                const transferAt = params.row.transfer_request_sent_at
                    ? new Date(params.row.transfer_request_sent_at).toLocaleDateString('en-CA')
                    : '';
                return transferTo ? (
                    <div>
                        <span style={{ fontSize: '0.7rem' }}>{transferTo}&nbsp;</span>
                        <Chip
                            label={transferAt}
                            color="primary"
                            sx={{
                                borderRadius: '2px',
                                fontSize: '0.6rem',
                                height: '16px',
                                '& span': {
                                    padding: '1px',
                                },
                            }}
                        />
                    </div>
                ) : (
                    ''
                );
            },
        },
        {
            field: 'transfer_request_accepted_at',
            headerName: 'Transfer Accepted On',
            width: 220,
            sortable: true,
            renderCell: (params: GridRenderCellParams<Pod>) => {
                const acceptedAt = params.row.transfer_request_accepted_at
                    ? new Date(params.row.transfer_request_accepted_at).toLocaleDateString('en-CA')
                    : '';
                // A transfer to an athlete with no active combo subscription has no
                // subscription_id to key off, so the explicit transfer marker counts too.
                const assignOrTransfer =
                    params.row.subscription_id || params.row.athlete_transferred_at
                        ? 'Transferred'
                        : 'Assigned';
                const hasSubscription = !!params.row.subscription_id;
                const hasActiveSubscription = params.row.has_active_subscription;

                if (!acceptedAt) return '';

                const chipIcon = hasSubscription ? (
                    hasActiveSubscription ? (
                        <CheckIcon style={{ color: 'white', fontSize: 12, marginRight: '2px' }} />
                    ) : (
                        <CloseIcon style={{ color: 'white', fontSize: 12, marginRight: '2px' }} />
                    )
                ) : undefined;

                const chip = (
                    <Chip
                        icon={chipIcon}
                        label={assignOrTransfer}
                        sx={{
                            borderRadius: '2px',
                            fontSize: '0.6rem',
                            height: '16px',
                            '& span': {
                                padding: '1px',
                                paddingRight: hasSubscription ? '4px' : '0px',
                            },
                            '& .MuiChip-icon': {
                                marginLeft: '4px',
                                marginRight: '-4px',
                            },
                            backgroundColor: hasSubscription ? 'green' : 'lightgray',
                            color: hasSubscription ? 'white' : 'inherit',
                        }}
                    />
                );

                return (
                    <div>
                        <span style={{ fontSize: '0.7rem' }}>{acceptedAt}&nbsp;</span>
                        {hasSubscription ? (
                            <a
                                href={`https://dashboard.stripe.com/subscriptions/${params.row.subscription_id}`}
                                target="_blank"
                                rel="noreferrer"
                                style={{ textDecoration: 'none' }}
                            >
                                {chip}
                            </a>
                        ) : (
                            chip
                        )}
                    </div>
                );
            },
        },
        {
            field: 'subscription_status',
            headerName: 'Subscription',
            width: 170,
            sortable: false,
            renderCell: (params: GridRenderCellParams<Pod>) => {
                const status = params.row.subscription_status;
                const startDate = params.row.subscription_started_at
                    ? new Date(params.row.subscription_started_at).toLocaleDateString('en-CA')
                    : null;
                if (!status && !startDate) return '';
                const colorMap: Record<string, string> = {
                    active: theme.palette.success.main,
                    past_due: theme.palette.warning.main,
                    canceled: theme.palette.error.main,
                    incomplete: theme.palette.grey[500],
                    trialing: theme.palette.info.main,
                };
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {status && (
                            <Chip
                                label={status.replace('_', ' ')}
                                sx={{
                                    borderRadius: '4px',
                                    fontSize: '0.6rem',
                                    height: '20px',
                                    backgroundColor: colorMap[status] ?? theme.palette.grey[500],
                                    color: 'white',
                                    fontWeight: 600,
                                    textTransform: 'capitalize',
                                    '& span': { padding: '0 6px' },
                                }}
                            />
                        )}
                        {startDate && (
                            <span style={{ fontSize: '0.65rem', color: '#666' }}>{startDate}</span>
                        )}
                    </Box>
                );
            },
        },
        {
            field: 'custody_status',
            headerName: 'Custody',
            width: 140,
            sortable: false,
            renderCell: (params: GridRenderCellParams<Pod>) => {
                const { label, colorKey } = getCustodyDisplay(params.row);

                if (!label) return '';

                const backgroundColor =
                    colorKey === 'blue'
                        ? theme.palette.primary.main
                        : colorKey === 'red'
                        ? theme.palette.error.main
                        : theme.palette.grey[500];

                return (
                    <Chip
                        label={label}
                        sx={{
                            borderRadius: '4px',
                            fontSize: '0.6rem',
                            height: '20px',
                            backgroundColor,
                            color: 'white',
                            fontWeight: 600,
                            '& span': { padding: '0 6px' },
                        }}
                    />
                );
            },
        },
        {
            field: 'org_subscription_quantity',
            headerName: 'Paid / Assigned',
            width: 120,
            sortable: false,
            renderCell: (params: GridRenderCellParams<Pod>) => {
                if (params.row.pod_type === 'athlete') return '';
                const qty = params.row.org_subscription_quantity;
                const podCount = params.row.org_pod_count;
                if (
                    qty === null ||
                    qty === undefined ||
                    podCount === null ||
                    podCount === undefined
                )
                    return '';
                const isCovered = qty >= podCount;
                return (
                    <Chip
                        label={`${qty}/${podCount}`}
                        sx={{
                            borderRadius: '4px',
                            fontSize: '0.6rem',
                            height: '20px',
                            backgroundColor: isCovered
                                ? theme.palette.success.main
                                : theme.palette.warning.main,
                            color: 'white',
                            fontWeight: 600,
                            '& span': { padding: '0 6px' },
                        }}
                    />
                );
            },
        },
        {
            field: 'recent_upload_count',
            headerName: 'Uploads (30d)',
            width: 110,
            sortable: false,
            renderCell: (params: GridRenderCellParams<Pod>) => {
                const count = params.row.recent_upload_count ?? 0;
                return (
                    <Chip
                        label={count}
                        sx={{
                            borderRadius: '4px',
                            fontSize: '0.6rem',
                            height: '20px',
                            backgroundColor:
                                count > 0 ? theme.palette.success.main : theme.palette.grey[400],
                            color: 'white',
                            fontWeight: 600,
                            '& span': { padding: '0 6px' },
                        }}
                    />
                );
            },
        },
        {
            field: 'created_at',
            headerName: 'Created At',
            width: 140,
            sortable: true,
            valueFormatter: ((params: number) => {
                return params ? new Date(params).toLocaleDateString('en-CA') : '';
            }) as GridValueFormatter<Pod>,
        },
        {
            field: 'updated_at',
            headerName: 'Updated At',
            width: 140,
            sortable: true,
            valueFormatter: ((params: number) => {
                return params ? new Date(params).toLocaleDateString('en-CA') : '';
            }) as GridValueFormatter<Pod>,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 175,
            renderCell: (params: GridRenderCellParams<Pod>) => (
                <>
                    <Button
                        onClick={() => handleUpdate(params.row)}
                        disabled={!editedPods[params.row.uuid]}
                        variant="contained"
                        size="small"
                        sx={{ marginRight: '4px', fontSize: '0.6rem' }}
                    >
                        UPDATE
                    </Button>
                    <PodActionsMenu pod={params.row} />
                </>
            ),
        },
        {
            field: 'bulk_actions',
            headerName: 'Bulk',
            width: 175,
            sortable: false,
            headerAlign: 'right',
            align: 'right',
            renderHeader: () => (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                    <Button
                        variant="contained"
                        size="small"
                        disabled={bulkSelectedPods.length === 0}
                        onClick={handleBulkActionClick}
                        sx={{ mr: 1, fontSize: '0.7rem' }}
                    >
                        Bulk Actions
                    </Button>
                    <Checkbox
                        size="small"
                        checked={headerCheckboxChecked}
                        onChange={handleHeaderCheckboxChange}
                    />
                </Box>
            ),
            renderCell: (params) => (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                    <Checkbox
                        size="small"
                        checked={bulkSelectedPods.includes(params.row.uuid)}
                        onChange={() => handleCheckboxChange(params.row.uuid)}
                    />
                </Box>
            ),
        },
    ];

    const BulkActionsModal = () => {
        // Move these states from parent PodList to here
        const [allOrganizationsForBulk, setAllOrganizationsForBulk] = useState<Organization[]>([]);
        const [bulkAssociateSimpleId, setBulkAssociateSimpleId] = useState('');
        const [bulkAssociateClubId, setBulkAssociateClubId] = useState('');
        const [bulkDisassociateSimpleId, setBulkDisassociateSimpleId] = useState('');
        const [bulkDisassociateClubId, setBulkDisassociateClubId] = useState('');
        const [selectedOrganizationForBulkPodAction, setSelectedOrganizationForBulkPodAction] =
            useState<Organization | null>(null);
        const [bulkExternalInvoiceId, setBulkExternalInvoiceId] = useState('');
        const [bulkExternalInvoiceNotes, setBulkExternalInvoiceNotes] = useState('');
        const [bulkExternalInvoiceEndDate, setBulkExternalInvoiceEndDate] = useState('');

        // ── Bulk Custody Status state ──────────────────────────────────────────
        const [bulkCustodyStatus, setBulkCustodyStatus] = useState<
            'shipped' | 'returned' | 'return_requested' | ''
        >('');
        const [bulkTrackingNumber, setBulkTrackingNumber] = useState('');
        const [bulkCustodyNotes, setBulkCustodyNotes] = useState('');
        const [custodyConfirmDialogOpen, setCustodyConfirmDialogOpen] = useState(false);
        // ──────────────────────────────────────────────────────────────────────

        // Move this function from parent into the modal component
        const fetchAllOrganizationsForBulk = debounce((searchTerm: string, perPage?: number) => {
            adminGetAllOrganizations(searchTerm, perPage)
                .then((response) => {
                    setAllOrganizationsForBulk(response.data.data);
                })
                .catch((error) => {
                    console.error('Failed to fetch organizations for bulk actions', error);
                });
        }, 300);

        // Fetch organizations when modal opens
        useEffect(() => {
            if (bulkActionModalOpen) {
                fetchAllOrganizationsForBulk('', 500);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [bulkActionModalOpen]);

        // New state for confirmation dialog
        const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
        const [confirmMessage, setConfirmMessage] = useState('');
        const [pendingAction, setPendingAction] = useState<
            'associate' | 'disassociate' | 'attachInvoice' | 'removeInvoice' | null
        >(null);

        // Filter pods to only show selected ones
        const selectedPodDetails = pods.filter((pod) => bulkSelectedPods.includes(pod.uuid));

        // Generate summary text
        const summaryText = selectedPodDetails
            .map(
                (pod) =>
                    `${pod.uuid} | ${pod.serial_number} | ${
                        pod.organization_name ?? 'No organization'
                    }\n`,
            )
            .join('');

        const refreshPodsAfterAction = async () => {
            const searchParams = {
                ...searchParamsRef.current,
                page: paginationModel.page + 1,
                per_page: paginationModel.pageSize,
            };
            await getPodSearchResults(searchParams);
            setBulkSelectedPods([]);
            setHeaderCheckboxChecked(false);
            handleCloseBulkActionModal();
        };

        const handleConfirmAction = async () => {
            try {
                if (pendingAction === 'associate' || pendingAction === 'disassociate') {
                    const params = {
                        pod_uuids: bulkSelectedPods,
                        simple_id:
                            pendingAction === 'associate'
                                ? bulkAssociateSimpleId
                                : bulkDisassociateSimpleId,
                        club_id:
                            pendingAction === 'associate'
                                ? bulkAssociateClubId
                                : bulkDisassociateClubId,
                        organization_uuid:
                            pendingAction === 'associate'
                                ? selectedOrganizationForBulkPodAction?.uuid || null
                                : null,
                    };
                    await bulkUpdatePods(params);
                } else if (pendingAction === 'attachInvoice') {
                    await bulkExternalInvoice({
                        pod_uuids: bulkSelectedPods,
                        external_invoice_id: bulkExternalInvoiceId,
                        external_invoice_notes: bulkExternalInvoiceNotes || undefined,
                        external_invoice_end_date: bulkExternalInvoiceEndDate,
                    });
                } else if (pendingAction === 'removeInvoice') {
                    await bulkRemoveExternalInvoice(bulkSelectedPods);
                }

                await refreshPodsAfterAction();
                setSnackbarMessage('Pods updated successfully!');
                setSnackbarOpen(true);
            } catch (error: any) {
                setSnackbarMessage(
                    `Failed to update pods: ${error.response?.data?.message || 'Unknown error'}`,
                );
                setSnackbarOpen(true);
            }
            setConfirmDialogOpen(false);
            setPendingAction(null);
        };

        // Handle form submissions
        const handleAssociate = () => {
            const message = `Do you want to bulk update ${bulkSelectedPods.length} pods to associate with organization "${selectedOrganizationForBulkPodAction?.name}" and have simple id "${bulkAssociateSimpleId}" and club id "${bulkAssociateClubId}"?`;
            setConfirmMessage(message);
            setPendingAction('associate');
            setConfirmDialogOpen(true);
        };

        const handleDisassociate = () => {
            const message = `Do you want to bulk update ${bulkSelectedPods.length} pods to disassociate from their organizations and have simple id "${bulkDisassociateSimpleId}" and club id "${bulkDisassociateClubId}"?`;
            setConfirmMessage(message);
            setPendingAction('disassociate');
            setConfirmDialogOpen(true);
        };

        // ── Bulk Custody Status helpers ────────────────────────────────────────
        const custodyStatusRequiresTracking = bulkCustodyStatus === 'shipped';

        const isBulkCustodyValid =
            !!bulkCustodyStatus && (!custodyStatusRequiresTracking || !!bulkTrackingNumber.trim());

        const custodyStatusLabel: Record<string, string> = {
            shipped: 'Shipped',
            returned: 'Returned',
            return_requested: 'Return Requested',
        };

        const handleBulkCustodyStatusClick = () => {
            setCustodyConfirmDialogOpen(true);
        };

        const handleConfirmBulkCustodyStatus = async () => {
            setCustodyConfirmDialogOpen(false);
            try {
                await Promise.all(
                    bulkSelectedPods.map((podUuid) => {
                        if (bulkCustodyStatus === 'shipped') {
                            return markPodShipped(podUuid, {
                                tracking_number: bulkTrackingNumber || undefined,
                                notes: bulkCustodyNotes || undefined,
                            });
                        } else if (bulkCustodyStatus === 'returned') {
                            return markPodReturned(podUuid, {
                                notes: bulkCustodyNotes || undefined,
                            });
                        } else if (bulkCustodyStatus === 'return_requested') {
                            return requestPodReturn(podUuid, {
                                notes: bulkCustodyNotes || undefined,
                            });
                        }
                        return Promise.resolve();
                    }),
                );

                const refreshParams = {
                    ...searchParamsRef.current,
                    page: paginationModel.page + 1,
                    per_page: paginationModel.pageSize,
                };
                await getPodSearchResults(refreshParams);

                setBulkSelectedPods([]);
                setHeaderCheckboxChecked(false);
                setSnackbarMessage(
                    `Custody status updated to "${custodyStatusLabel[bulkCustodyStatus]}" for ${bulkSelectedPods.length} pod(s)!`,
                );
                setSnackbarOpen(true);
                handleCloseBulkActionModal();
            } catch (error: any) {
                setSnackbarMessage(
                    `Failed to update custody status: ${
                        error.response?.data?.message || 'Unknown error'
                    }`,
                );
                setSnackbarOpen(true);
            }
        };
        // ──────────────────────────────────────────────────────────────────────
        const handleAttachInvoice = () => {
            const message = `Attach external invoice "${bulkExternalInvoiceId}" (expires ${bulkExternalInvoiceEndDate}) to ${bulkSelectedPods.length} pod(s)?`;
            setConfirmMessage(message);
            setPendingAction('attachInvoice');
            setConfirmDialogOpen(true);
        };

        const handleRemoveInvoice = () => {
            const message = `Remove external invoice from ${bulkSelectedPods.length} pod(s)?`;
            setConfirmMessage(message);
            setPendingAction('removeInvoice');
            setConfirmDialogOpen(true);
        };

        return (
            <>
                <Dialog
                    open={confirmDialogOpen}
                    onClose={() => setConfirmDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Confirm Action</DialogTitle>
                    <DialogContent>
                        <DialogContentText>{confirmMessage}</DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmDialogOpen(false)} variant="outlined">
                            Cancel
                        </Button>
                        <Button onClick={handleConfirmAction} variant="contained">
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Confirm bulk custody status change */}
                <Dialog
                    open={custodyConfirmDialogOpen}
                    onClose={() => setCustodyConfirmDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Confirm Custody Status Change</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            You are about to mark <strong>{bulkSelectedPods.length}</strong> pod(s)
                            as{' '}
                            <strong>
                                {custodyStatusLabel[bulkCustodyStatus] ?? bulkCustodyStatus}
                            </strong>
                            .
                            {bulkCustodyStatus === 'shipped' && bulkTrackingNumber && (
                                <>
                                    {' '}
                                    Tracking number: <strong>{bulkTrackingNumber}</strong>.
                                </>
                            )}
                            {bulkCustodyNotes && (
                                <>
                                    {' '}
                                    Notes: <strong>{bulkCustodyNotes}</strong>.
                                </>
                            )}{' '}
                            This action cannot be undone automatically. Continue?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setCustodyConfirmDialogOpen(false)}
                            variant="outlined"
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleConfirmBulkCustodyStatus} variant="contained">
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={bulkActionModalOpen}
                    onClose={handleCloseBulkActionModal}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle sx={{ fontSize: '1.4rem', fontWeight: '600' }}>
                        Bulk Actions
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ my: 2 }}>
                            <Typography sx={{ fontWeight: '600' }}>
                                Selected Pods: {bulkSelectedPods.length}
                            </Typography>

                            <TextField
                                multiline
                                rows={4}
                                value={summaryText}
                                fullWidth
                                variant="outlined"
                                sx={{ mt: 2, mb: 3 }}
                                InputProps={{ readOnly: true }}
                            />

                            {/* Associate Controls */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    mb: 3,
                                    alignItems: 'flex-start',
                                    marginTop: '16px',
                                }}
                            >
                                <Box sx={{ flex: 2 }}>
                                    <Autocomplete
                                        options={allOrganizationsForBulk}
                                        getOptionLabel={(option) =>
                                            `${option.name} (${option.subscription_tier?.name}) - ${option.uuid}`
                                        }
                                        onInputChange={(event, newInputValue, reason) => {
                                            if (reason !== 'reset') {
                                                fetchAllOrganizationsForBulk(newInputValue);
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select Organization"
                                                size="small"
                                            />
                                        )}
                                        value={
                                            allOrganizationsForBulk.find(
                                                (org) =>
                                                    org.uuid ===
                                                    selectedOrganizationForBulkPodAction?.uuid,
                                            ) || null
                                        }
                                        onChange={(_, newValue) => {
                                            setSelectedOrganizationForBulkPodAction(newValue);
                                        }}
                                        isOptionEqualToValue={(option, value) =>
                                            option.uuid === value?.uuid
                                        }
                                        size="small"
                                    />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <TextField
                                        required
                                        label="Simple ID"
                                        value={bulkAssociateSimpleId}
                                        onChange={(e) => setBulkAssociateSimpleId(e.target.value)}
                                        fullWidth
                                        size="small"
                                    />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <TextField
                                        required
                                        label="Club ID"
                                        value={bulkAssociateClubId}
                                        onChange={(e) => setBulkAssociateClubId(e.target.value)}
                                        fullWidth
                                        size="small"
                                    />
                                </Box>
                                <Button
                                    variant="contained"
                                    onClick={handleAssociate}
                                    disabled={
                                        !bulkAssociateSimpleId ||
                                        !bulkAssociateClubId ||
                                        !selectedOrganizationForBulkPodAction
                                    }
                                >
                                    Attach Org
                                </Button>
                            </Box>

                            {/* Disassociate Controls */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'flex-start',
                                    marginTop: '8px',
                                }}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <TextField
                                        required
                                        label="Simple ID"
                                        value={bulkDisassociateSimpleId}
                                        onChange={(e) =>
                                            setBulkDisassociateSimpleId(e.target.value)
                                        }
                                        fullWidth
                                        size="small"
                                    />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <TextField
                                        required
                                        label="Club ID"
                                        value={bulkDisassociateClubId}
                                        onChange={(e) => setBulkDisassociateClubId(e.target.value)}
                                        fullWidth
                                        size="small"
                                    />
                                </Box>
                                <Box sx={{ flex: 2 }} /> {/* Spacer to align with top row */}
                                <Button
                                    variant="contained"
                                    onClick={handleDisassociate}
                                    disabled={!bulkDisassociateSimpleId || !bulkDisassociateClubId}
                                >
                                    Detach Org
                                </Button>
                            </Box>

                            {/* ── Bulk Custody Status ─────────────────────────────────────────── */}
                            <Divider sx={{ my: 3 }} />
                            <Typography sx={{ fontWeight: '600', mb: 2 }}>
                                Update Custody Status
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'flex-start',
                                    flexWrap: 'wrap',
                                }}
                            >
                                {/* Status selector */}
                                <Box sx={{ flex: '1 1 160px', minWidth: 160 }}>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel id="bulk-custody-status-label">
                                            Custody Status
                                        </InputLabel>
                                        <Select
                                            labelId="bulk-custody-status-label"
                                            label="Custody Status"
                                            value={bulkCustodyStatus}
                                            onChange={(e) =>
                                                setBulkCustodyStatus(
                                                    e.target.value as typeof bulkCustodyStatus,
                                                )
                                            }
                                        >
                                            <MenuItem value="">
                                                <em>— Select —</em>
                                            </MenuItem>
                                            <MenuItem value="shipped">
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <LocalShippingIcon fontSize="small" />
                                                    Shipped
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="returned">
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <InventoryIcon fontSize="small" />
                                                    Returned
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="return_requested">
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <AssignmentReturnIcon fontSize="small" />
                                                    Return Requested
                                                </Box>
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>

                                {/* Tracking number — required only when status is "Shipped" */}
                                <Box sx={{ flex: '2 1 200px', minWidth: 200 }}>
                                    <TextField
                                        label={
                                            custodyStatusRequiresTracking
                                                ? 'Tracking Number *'
                                                : 'Tracking Number'
                                        }
                                        value={bulkTrackingNumber}
                                        onChange={(e) => setBulkTrackingNumber(e.target.value)}
                                        fullWidth
                                        size="small"
                                        disabled={!bulkCustodyStatus}
                                        required={custodyStatusRequiresTracking}
                                        helperText={
                                            custodyStatusRequiresTracking
                                                ? 'Required for Shipped status'
                                                : 'Optional'
                                        }
                                    />
                                </Box>

                                {/* Notes */}
                                <Box sx={{ flex: '2 1 200px', minWidth: 200 }}>
                                    <TextField
                                        label="Notes"
                                        value={bulkCustodyNotes}
                                        onChange={(e) => setBulkCustodyNotes(e.target.value)}
                                        fullWidth
                                        size="small"
                                        disabled={!bulkCustodyStatus}
                                        helperText="Optional"
                                    />
                                </Box>

                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleBulkCustodyStatusClick}
                                    disabled={!isBulkCustodyValid}
                                    startIcon={<LocalShippingIcon />}
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        alignSelf: 'flex-start',
                                        mt: 0.25,
                                    }}
                                >
                                    Update Status
                                </Button>
                            </Box>
                            {/* ──────────────────────────────────────────────────────────────── */}

                            {/* External Invoice Section */}
                            <Divider sx={{ my: 3 }} />
                            <Typography sx={{ fontWeight: '600', mb: 2 }}>
                                External Invoice
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    mb: 2,
                                    alignItems: 'flex-start',
                                }}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <TextField
                                        label="Stripe Invoice ID"
                                        value={bulkExternalInvoiceId}
                                        onChange={(e) => setBulkExternalInvoiceId(e.target.value)}
                                        fullWidth
                                        size="small"
                                        placeholder="in_..."
                                    />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <TextField
                                        label="Coverage End Date"
                                        type="date"
                                        value={bulkExternalInvoiceEndDate}
                                        onChange={(e) =>
                                            setBulkExternalInvoiceEndDate(e.target.value)
                                        }
                                        fullWidth
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <TextField
                                        label="Notes"
                                        value={bulkExternalInvoiceNotes}
                                        onChange={(e) =>
                                            setBulkExternalInvoiceNotes(e.target.value)
                                        }
                                        fullWidth
                                        size="small"
                                    />
                                </Box>
                                <Button
                                    variant="contained"
                                    onClick={handleAttachInvoice}
                                    disabled={!bulkExternalInvoiceId || !bulkExternalInvoiceEndDate}
                                    startIcon={<ReceiptLongIcon />}
                                >
                                    Attach Invoice
                                </Button>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={handleRemoveInvoice}
                                >
                                    Remove Invoice
                                </Button>
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="contained" onClick={handleCloseBulkActionModal}>
                            Cancel
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    };

    const PodActionsMenu = ({ pod }: { pod: Pod }) => {
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
        const open = Boolean(anchorEl);
        const [isChangingOrganization, setIsChangingOrganization] = useState<boolean>(false);
        const [selectedOrgForChange, setSelectedOrgForChange] = useState<Organization | null>(null);
        const [organizationsForOrgChangeModal, setOrganizationsForOrgChangeModal] = useState<
            Organization[]
        >([]);
        const [isTransferringIndividual, setIsTransferringIndividual] = useState<boolean>(false);
        const [isAssigningIndividual, setIsAssigningIndividual] = useState<boolean>(false);
        const [selectedIndividualForAssign, setSelectedIndividualForAssign] =
            useState<Individual | null>(null);
        const [individualsForChangeModal, setIndividualsForChangeModal] = useState<Individual[]>(
            [],
        );
        const [selectedIndividualForPodUpdate, setSelectedIndividualForPodUpdate] =
            useState<Individual | null>(null);
        const [isDetachingIndividual, setIsDetachingIndividual] = useState<boolean>(false);
        const [isViewingHistory, setIsViewingHistory] = useState<boolean>(false);
        const [podHistory, setPodHistory] = useState<any[]>([]);
        const [isMarkingShipped, setIsMarkingShipped] = useState(false);
        const [isMarkingReturned, setIsMarkingReturned] = useState(false);
        const [isRequestingReturn, setIsRequestingReturn] = useState(false);
        const [custodyTrackingNumber, setCustodyTrackingNumber] = useState('');
        const [custodyNotes, setCustodyNotes] = useState('');
        const [recommissionConfirmOpen, setRecommissionConfirmOpen] = useState(false);
        const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
        };

        const handleClose = () => {
            setAnchorEl(null);
        };

        /**
         * Organization change option
         */
        const handleChangeOrganization = () => {
            handleClose();
            handleOpenOrgChangeDialog();
        };

        const fetchOrganizationsOrgChangeModal = debounce(
            (searchTerm: string, perPage?: number) => {
                adminGetAllOrganizations(searchTerm, perPage)
                    .then((response) => {
                        setOrganizationsForOrgChangeModal(response.data.data);
                    })
                    .catch((error) => {
                        console.error('Failed to fetch organizations', error);
                    });
            },
            300,
        );

        const handleOpenOrgChangeDialog = () => {
            fetchOrganizationsOrgChangeModal('', 100);
            setIsChangingOrganization(true);
        };

        const handleCloseOrgChangeDialog = () => setIsChangingOrganization(false);

        const handleOrgChangeOrgChangeDialog = async () => {
            try {
                await changePodOrganization(pod.uuid, selectedOrgForChange?.uuid || null);
                const params = {
                    ...searchParamsRef.current,
                    page: paginationModel.page + 1,
                    per_page: paginationModel.pageSize,
                };
                await getPodSearchResults(params);
                handleCloseOrgChangeDialog();
                setSnackbarMessage('Pod organization changed successfully!');
                setSnackbarOpen(true);
            } catch (error: any) {
                console.error('Failed to change pod organization', error);
                openDialogModal(
                    'Error',
                    `Failed to change pod organization: ${error.response.data.message}`,
                );
            }
        };

        // Rename existing function
        const handleTransferIndividual = () => {
            handleClose();
            fetchIndividualChangeModal('');
            setIsTransferringIndividual(true);
        };

        // Add new assign handlers
        const handleAssignIndividual = () => {
            handleClose();
            fetchIndividualChangeModal('');
            setIsAssigningIndividual(true);
        };

        const handleCloseAssignIndividualDialog = () => {
            setIsAssigningIndividual(false);
            setSelectedIndividualForAssign(null);
        };

        const handleConfirmAssignIndividual = async () => {
            if (selectedIndividualForAssign) {
                try {
                    await assignPodToIndividual(pod.uuid, selectedIndividualForAssign.uuid);
                    const params = {
                        ...searchParamsRef.current,
                        page: paginationModel.page + 1,
                        per_page: paginationModel.pageSize,
                    };
                    await getPodSearchResults(params);
                    handleCloseAssignIndividualDialog();
                    setSnackbarMessage('Individual assigned successfully!');
                    setSnackbarOpen(true);
                } catch (error: any) {
                    console.error('Failed to assign individual for pod', error);
                    openDialogModal(
                        'Error',
                        `Failed to assign pod individual: ${error.response.data.message}`,
                    );
                }
            }
        };

        const fetchIndividualChangeModal = debounce((name: string) => {
            adminGetIndividuals({
                'filter[organization]': pod.organization_uuid,
                'filter[name]': name,
                'filter[has_managing_user]': 1,
            })
                .then((response) => {
                    setIndividualsForChangeModal(response.data.data);
                })
                .catch((error) => {
                    console.error('Failed to fetch athletes', error);
                });
        }, 300);

        const handleCloseIndividualTransferDialog = () => setIsTransferringIndividual(false);

        const handleChangeIndividualTransferDialog = async () => {
            if (selectedIndividualForPodUpdate) {
                try {
                    await transferPodToIndividual(pod.uuid, selectedIndividualForPodUpdate.uuid);
                    const params = {
                        ...searchParamsRef.current,
                        page: paginationModel.page + 1,
                        per_page: paginationModel.pageSize,
                    };
                    await getPodSearchResults(params);
                    handleCloseIndividualTransferDialog();
                    setSnackbarMessage('Individual transferred successfully!');
                    setSnackbarOpen(true);
                } catch (error: any) {
                    console.error('Failed to transfer individual for pod', error);
                    openDialogModal(
                        'Error',
                        `Failed to transfer pod: ${error.response.data.message}`,
                    );
                }
            }
        };

        /**
         * Detach individual option.
         */
        const handleDetachIndividual = () => {
            handleClose();
            setIsDetachingIndividual(true);
        };

        const handleDetachIndividualDialog = async () => {
            try {
                await detachPodIndividual(pod.uuid);
                const params = {
                    ...searchParamsRef.current,
                    page: paginationModel.page + 1,
                    per_page: paginationModel.pageSize,
                };
                await getPodSearchResults(params);
                setIsDetachingIndividual(false);
                setSnackbarMessage('Individual detached from pod!');
                setSnackbarOpen(true);
            } catch (error: any) {
                console.error('Failed to detach individual for pod', error);
                openDialogModal(
                    'Error',
                    `Failed to detach pod individual: ${error.response.data.message}`,
                );
            }
        };

        const resetCustodyForm = () => {
            setCustodyTrackingNumber('');
            setCustodyNotes('');
        };

        const handleMarkShipped = () => {
            handleClose();
            resetCustodyForm();
            setIsMarkingShipped(true);
        };

        const handleConfirmMarkShipped = async () => {
            try {
                await markPodShipped(pod.uuid, {
                    tracking_number: custodyTrackingNumber || undefined,
                    notes: custodyNotes || undefined,
                });
                const params = {
                    ...searchParamsRef.current,
                    page: paginationModel.page + 1,
                    per_page: paginationModel.pageSize,
                };
                await getPodSearchResults(params);
                setIsMarkingShipped(false);
                setSnackbarMessage('Pod marked as shipped!');
                setSnackbarOpen(true);
            } catch (error: any) {
                openDialogModal(
                    'Error',
                    `Failed to mark pod as shipped: ${
                        error.response?.data?.message || error.message
                    }`,
                );
            }
        };

        const handleMarkReturned = () => {
            handleClose();
            resetCustodyForm();
            setIsMarkingReturned(true);
        };

        const handleConfirmMarkReturned = async () => {
            try {
                await markPodReturned(pod.uuid, {
                    notes: custodyNotes || undefined,
                });
                const params = {
                    ...searchParamsRef.current,
                    page: paginationModel.page + 1,
                    per_page: paginationModel.pageSize,
                };
                await getPodSearchResults(params);
                setIsMarkingReturned(false);
                setSnackbarMessage('Pod marked as returned!');
                setSnackbarOpen(true);
            } catch (error: any) {
                openDialogModal(
                    'Error',
                    `Failed to mark pod as returned: ${
                        error.response?.data?.message || error.message
                    }`,
                );
            }
        };

        const handleRequestReturn = () => {
            handleClose();
            resetCustodyForm();
            setIsRequestingReturn(true);
        };

        const handleConfirmRequestReturn = async () => {
            try {
                await requestPodReturn(pod.uuid, {
                    notes: custodyNotes || undefined,
                });
                const params = {
                    ...searchParamsRef.current,
                    page: paginationModel.page + 1,
                    per_page: paginationModel.pageSize,
                };
                await getPodSearchResults(params);
                setIsRequestingReturn(false);
                setSnackbarMessage('Return requested for pod!');
                setSnackbarOpen(true);
            } catch (error: any) {
                openDialogModal(
                    'Error',
                    `Failed to request pod return: ${
                        error.response?.data?.message || error.message
                    }`,
                );
            }
        };

        const handleToggleComp = async () => {
            handleClose();
            try {
                await togglePodComp(pod.uuid);
                const params = {
                    ...searchParamsRef.current,
                    page: paginationModel.page + 1,
                    per_page: paginationModel.pageSize,
                };
                await getPodSearchResults(params);
                setSnackbarMessage(pod.is_comp ? 'Pod unmarked as comp!' : 'Pod marked as comp!');
                setSnackbarOpen(true);
            } catch (error: any) {
                openDialogModal(
                    'Error',
                    `Failed to toggle comp status: ${
                        error.response?.data?.message || error.message
                    }`,
                );
            }
        };

        const executeDecommissionToggle = async () => {
            const wasDecommissioned = !!pod.decommissioned_at;
            try {
                await togglePodDecommission(pod.uuid);
                const params = {
                    ...searchParamsRef.current,
                    page: paginationModel.page + 1,
                    per_page: paginationModel.pageSize,
                };
                await getPodSearchResults(params);
                refreshCounts();
                setRecommissionConfirmOpen(false);
                setSnackbarMessage(
                    wasDecommissioned ? 'Pod recommissioned!' : 'Pod decommissioned!',
                );
                setSnackbarOpen(true);
            } catch (error: any) {
                setRecommissionConfirmOpen(false);
                openDialogModal(
                    'Error',
                    `Failed to toggle decommission status: ${
                        error.response?.data?.message || error.message
                    }`,
                );
            }
        };

        const handleDecommissionMenuClick = () => {
            handleClose();
            if (pod.decommissioned_at) {
                setRecommissionConfirmOpen(true);
            } else {
                void executeDecommissionToggle();
            }
        };

        /**
         * View history option.
         */
        const fetchPodHistory = () => {
            getPodHistory(pod.uuid)
                .then((response: any) => {
                    setPodHistory(response.data);
                })
                .catch((error: any) => {
                    console.error('Failed to fetch pod history', error);
                });
        };

        const handleViewHistory = () => {
            fetchPodHistory();
            setIsViewingHistory(true);
        };

        const podHistoryColumns: GridColDef[] = [
            { field: 'event', headerName: 'Event', flex: 1 },
            {
                field: 'organization',
                headerName: 'Organization',
                flex: 1,
                renderCell: (params: GridRenderCellParams) => {
                    const { organization_name, new_organization_name } = params.row;
                    if (organization_name && new_organization_name) {
                        return `${organization_name} -> ${new_organization_name}`;
                    }
                    return organization_name || '';
                },
            },
            { field: 'individual_name', headerName: 'Individual', flex: 1 },
            {
                field: 'user',
                headerName: 'User',
                flex: 1,
                renderCell: (params: GridRenderCellParams) => {
                    return params.row.full_name
                        ? `${params.row.full_name} (${params.row.user_name})`
                        : '';
                },
            },
            {
                field: 'event_properties',
                headerName: 'Event Properties',
                flex: 1,
                renderCell: (params: GridRenderCellParams) => (
                    <Tooltip title={<Typography>{JSON.stringify(params.value)}</Typography>} arrow>
                        <Typography
                            noWrap
                            style={{
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                display: 'flex',
                                alignItems: 'center',
                                height: '100%',
                            }}
                        >
                            {JSON.stringify(params.value)}
                        </Typography>
                    </Tooltip>
                ),
            },
            {
                field: 'meta_data',
                headerName: 'Meta Data',
                flex: 1,
                renderCell: (params: GridRenderCellParams) => (
                    <Tooltip title={<Typography>{JSON.stringify(params.value)}</Typography>} arrow>
                        <Typography
                            noWrap
                            style={{
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                display: 'flex',
                                alignItems: 'center',
                                height: '100%',
                            }}
                        >
                            {JSON.stringify(params.value)}
                        </Typography>
                    </Tooltip>
                ),
            },
            { field: 'created_at', headerName: 'Created At', flex: 1 },
        ];

        return (
            <>
                <Button
                    aria-controls={open ? 'pod-actions-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                >
                    <MoreVertIcon />
                </Button>
                <Menu id="pod-actions-menu" anchorEl={anchorEl} open={open} onClose={handleClose}>
                    <MenuItem onClick={handleViewHistory} style={{ fontSize: '0.8rem' }}>
                        <ClockIcon fontSize="small" style={{ marginRight: 8 }} />
                        View History
                    </MenuItem>
                    <MenuItem onClick={handleChangeOrganization} style={{ fontSize: '0.8rem' }}>
                        <BusinessIcon fontSize="small" style={{ marginRight: 8 }} />
                        Change Organization
                    </MenuItem>
                    <MenuItem onClick={handleTransferIndividual} style={{ fontSize: '0.8rem' }}>
                        <SendIcon fontSize="small" style={{ marginRight: 8 }} />
                        Transfer to Athlete
                    </MenuItem>
                    <MenuItem onClick={handleAssignIndividual} style={{ fontSize: '0.8rem' }}>
                        <AssignmentIndIcon fontSize="small" style={{ marginRight: 8 }} />
                        Assign Athlete
                    </MenuItem>
                    {pod.athlete_name && (
                        <MenuItem onClick={handleDetachIndividual} style={{ fontSize: '0.8rem' }}>
                            <PersonIcon fontSize="small" style={{ marginRight: 8 }} />
                            Detach Athlete
                        </MenuItem>
                    )}
                    <MenuItem onClick={handleMarkShipped} style={{ fontSize: '0.8rem' }} divider>
                        <LocalShippingIcon fontSize="small" style={{ marginRight: 8 }} />
                        Mark as Shipped
                    </MenuItem>
                    <MenuItem onClick={handleMarkReturned} style={{ fontSize: '0.8rem' }}>
                        <InventoryIcon fontSize="small" style={{ marginRight: 8 }} />
                        Mark as Returned
                    </MenuItem>
                    <MenuItem onClick={handleRequestReturn} style={{ fontSize: '0.8rem' }}>
                        <AssignmentReturnIcon fontSize="small" style={{ marginRight: 8 }} />
                        Request Return
                    </MenuItem>
                    <MenuItem onClick={handleToggleComp} style={{ fontSize: '0.8rem' }}>
                        <CardGiftcardIcon fontSize="small" style={{ marginRight: 8 }} />
                        {pod.is_comp ? 'Unmark as Comp' : 'Mark as Comp'}
                    </MenuItem>
                    <MenuItem
                        onClick={handleDecommissionMenuClick}
                        style={{
                            fontSize: '0.8rem',
                            color: pod.decommissioned_at ? undefined : '#d32f2f',
                        }}
                        divider
                    >
                        <BlockIcon
                            fontSize="small"
                            style={{
                                marginRight: 8,
                                color: pod.decommissioned_at ? undefined : '#d32f2f',
                            }}
                        />
                        {pod.decommissioned_at ? 'Recommission Pod' : 'Decommission Pod'}
                    </MenuItem>
                </Menu>
                <Dialog
                    open={recommissionConfirmOpen}
                    onClose={() => setRecommissionConfirmOpen(false)}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>Recommission pod?</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to recommission this pod? It will be treated as
                            active inventory again (including in-stock counts where applicable).
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setRecommissionConfirmOpen(false)}
                            variant="outlined"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => void executeDecommissionToggle()}
                            variant="contained"
                        >
                            Recommission
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={isChangingOrganization}
                    onClose={handleCloseOrgChangeDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Change Organization</DialogTitle>
                    <DialogContent>
                        <Autocomplete
                            options={organizationsForOrgChangeModal}
                            getOptionLabel={(option) =>
                                `${option.name} (${option.subscription_tier?.name}) - ${option.uuid}`
                            }
                            renderInput={(params) => (
                                <TextField {...params} label="Select Organization" />
                            )}
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason !== 'reset') {
                                    fetchOrganizationsOrgChangeModal(newInputValue);
                                }
                            }}
                            onChange={(_, newValue) => setSelectedOrgForChange(newValue)}
                            value={selectedOrgForChange}
                            size="small"
                            sx={{ marginTop: '8px' }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseOrgChangeDialog} variant="contained">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleOrgChangeOrgChangeDialog}
                            disabled={!selectedOrgForChange}
                            variant="contained"
                        >
                            Change
                        </Button>
                        <Button
                            onClick={handleOrgChangeOrgChangeDialog}
                            disabled={!pod.organization_uuid || !!selectedOrgForChange}
                            variant="contained"
                        >
                            Detach
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={isTransferringIndividual}
                    onClose={handleCloseIndividualTransferDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Transfer Pod to Athlete</DialogTitle>
                    <DialogContent>
                        <Autocomplete
                            options={individualsForChangeModal}
                            getOptionLabel={(option) =>
                                `${option.name} (${option.managing_user?.first_name || ''} ${
                                    option.managing_user?.last_name || ''
                                }) (${option.uuid})`
                            }
                            renderInput={(params) => (
                                <TextField {...params} label="Select Individual" />
                            )}
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason !== 'reset') {
                                    fetchIndividualChangeModal(newInputValue);
                                }
                            }}
                            onChange={(_, newValue) => setSelectedIndividualForPodUpdate(newValue)}
                            value={selectedIndividualForPodUpdate}
                            size={'small'}
                            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                            renderOption={(props, option) => (
                                <li
                                    {...props}
                                    key={option.uuid}
                                    style={{ display: 'flex', alignItems: 'center' }}
                                >
                                    {option.image_urls.avatar ? (
                                        <img
                                            src={option.image_urls.avatar}
                                            alt={option.name}
                                            style={{
                                                marginRight: 8,
                                                width: 24,
                                                height: 24,
                                            }}
                                        />
                                    ) : (
                                        // Optional: Display a default image or leave as is for no image
                                        <div
                                            style={{
                                                marginRight: 8,
                                                width: 24,
                                                height: 24,
                                            }}
                                        ></div>
                                    )}
                                    {option.name} ({option.managing_user?.first_name || ''}{' '}
                                    {option.managing_user?.last_name || ''}
                                    {' - '}
                                    {option.managing_user?.user_name})
                                </li>
                            )}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseIndividualTransferDialog} variant="contained">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleChangeIndividualTransferDialog}
                            disabled={!selectedIndividualForPodUpdate}
                            variant="contained"
                        >
                            Change
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={isAssigningIndividual}
                    onClose={handleCloseAssignIndividualDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Assign Pod to Athlete</DialogTitle>
                    <DialogContent>
                        <Autocomplete
                            options={individualsForChangeModal}
                            getOptionLabel={(option) =>
                                `${option.name} (${option.managing_user?.first_name || ''} ${
                                    option.managing_user?.last_name || ''
                                }) (${option.uuid})`
                            }
                            renderInput={(params) => (
                                <TextField {...params} label="Select Individual" />
                            )}
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason !== 'reset') {
                                    fetchIndividualChangeModal(newInputValue);
                                }
                            }}
                            onChange={(_, newValue) => setSelectedIndividualForAssign(newValue)}
                            value={selectedIndividualForAssign}
                            size={'small'}
                            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                            renderOption={(props, option) => (
                                <li
                                    {...props}
                                    key={option.uuid}
                                    style={{ display: 'flex', alignItems: 'center' }}
                                >
                                    {option.image_urls.avatar ? (
                                        <img
                                            src={option.image_urls.avatar}
                                            alt={option.name}
                                            style={{ marginRight: 8, width: 24, height: 24 }}
                                        />
                                    ) : (
                                        <div
                                            style={{ marginRight: 8, width: 24, height: 24 }}
                                        ></div>
                                    )}
                                    {option.name} ({option.managing_user?.first_name || ''}{' '}
                                    {option.managing_user?.last_name || ''} -{' '}
                                    {option.managing_user?.user_name})
                                </li>
                            )}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseAssignIndividualDialog} variant="contained">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmAssignIndividual}
                            disabled={!selectedIndividualForAssign}
                            variant="contained"
                        >
                            Assign
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={isDetachingIndividual}
                    onClose={() => setIsDetachingIndividual(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Are you sure?</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to detach this individual from this pod?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setIsDetachingIndividual(false)} variant="contained">
                            Cancel
                        </Button>
                        <Button onClick={handleDetachIndividualDialog} variant="contained">
                            Detach
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={isViewingHistory}
                    onClose={() => setIsViewingHistory(false)}
                    maxWidth="xl"
                    fullWidth
                >
                    <DialogTitle>Pod History</DialogTitle>
                    <DialogContent>
                        <DataGrid
                            rows={podHistory}
                            columns={podHistoryColumns}
                            getRowId={(row) => `${row.aggregate_uuid}-${row.aggregate_version}`}
                            autoHeight
                            disableColumnMenu
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setIsViewingHistory(false)} variant="contained">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={isMarkingShipped}
                    onClose={() => setIsMarkingShipped(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Mark Pod as Shipped</DialogTitle>
                    <DialogContent>
                        <TextField
                            label="Tracking Number"
                            value={custodyTrackingNumber}
                            onChange={(e) => setCustodyTrackingNumber(e.target.value)}
                            fullWidth
                            size="small"
                            sx={{ mt: 1, mb: 2 }}
                        />
                        <TextField
                            label="Notes"
                            value={custodyNotes}
                            onChange={(e) => setCustodyNotes(e.target.value)}
                            fullWidth
                            size="small"
                            multiline
                            rows={3}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setIsMarkingShipped(false)} variant="outlined">
                            Cancel
                        </Button>
                        <Button onClick={handleConfirmMarkShipped} variant="contained">
                            Mark Shipped
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={isMarkingReturned}
                    onClose={() => setIsMarkingReturned(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Mark Pod as Returned</DialogTitle>
                    <DialogContent>
                        <TextField
                            label="Notes"
                            value={custodyNotes}
                            onChange={(e) => setCustodyNotes(e.target.value)}
                            fullWidth
                            size="small"
                            multiline
                            rows={3}
                            sx={{ mt: 1 }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setIsMarkingReturned(false)} variant="outlined">
                            Cancel
                        </Button>
                        <Button onClick={handleConfirmMarkReturned} variant="contained">
                            Mark Returned
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={isRequestingReturn}
                    onClose={() => setIsRequestingReturn(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Request Pod Return</DialogTitle>
                    <DialogContent>
                        <TextField
                            label="Notes"
                            value={custodyNotes}
                            onChange={(e) => setCustodyNotes(e.target.value)}
                            fullWidth
                            size="small"
                            multiline
                            rows={3}
                            sx={{ mt: 1 }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setIsRequestingReturn(false)} variant="outlined">
                            Cancel
                        </Button>
                        <Button onClick={handleConfirmRequestReturn} variant="contained">
                            Request Return
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    };

    const handleRowUpdate = async (newRow: Pod, oldRow: Pod) => {
        if (newRow.simple_id !== oldRow.simple_id || newRow.club_id !== oldRow.club_id) {
            try {
                const updatedFields = {
                    simple_id: newRow.simple_id,
                    club_id: newRow.club_id,
                };
                await updatePod(newRow.uuid, updatedFields);
                return newRow;
            } catch (error) {
                console.error(`Failed to update pod`, error);
                openDialogModal('Error', `Failed to update pod`);
                return oldRow;
            }
        }
        return newRow;
    };

    const handleSearchParameterChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setSearchParams({
            ...searchParams,
            [event.target.name as string]: event.target.value,
        });
    };

    const handlePodSearch = async (event: React.FormEvent) => {
        event.preventDefault();
        try {
            const params: GetPodsParams = {
                ...searchParams,
                organization_uuid: searchParams.organization_uuid || undefined,
                page: paginationModel.page + 1,
                per_page: paginationModel.pageSize,
            };
            if (activeSubFilter === 'activeAthlete') {
                params.has_active_sub = true;
            } else if (activeSubFilter === 'activeOrg') {
                params.active_org_sub = true;
            } else if (activeSubFilter === 'partialOrg') {
                params.partial_org_sub = true;
            } else if (activeSubFilter === 'pastDue') {
                params.past_due = true;
            } else if (activeSubFilter === 'pastDueInUse') {
                params.past_due_in_use = true;
            } else if (activeSubFilter === 'comp') {
                params.is_comp = true;
            } else if (activeSubFilter === 'inStock') {
                params.in_stock = true;
            } else if (activeSubFilter === 'inStockDecommissioned') {
                params.in_stock_decommissioned = true;
            }
            await getPodSearchResults(params);
        } catch (error) {
            console.error(error);
        }
    };

    const getPodSearchResults = async (params: any) => {
        const paginatedParams = {
            ...params,
            page: paginationModel.page + 1,
            per_page: paginationModel.pageSize,
        };
        const response = await getPods(paginatedParams);
        console.debug('Pods response', response.data.data);
        if (Array.isArray(response.data.data)) {
            const updatedPods = response.data.data.map((pod: Pod) => {
                const transferRequestAcceptedAt = pod.transfer_request_accepted_at
                    ? new Date(pod.transfer_request_accepted_at).toLocaleString('en-US', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                      })
                    : '';
                return {
                    ...pod,
                    created_at: new Date(pod.created_at),
                    transfer_request_accepted_at: transferRequestAcceptedAt,
                };
            });
            setPods(updatedPods);
            setTotalRows(response.data.meta.total);
        } else {
            console.error('Unexpected data format: pods data is not an array');
        }
    };

    const handlePodSort = async (model: GridSortModel) => {
        let sortField = model[0]?.field;
        const sortOrder = model[0]?.sort;

        /**
         * Sort will be undefined, or it will be ascending, ie "createdAt", or in this case
         * it will be descending, ie "-createdAt". If descending, we prepend a hyphen.
         */
        if (sortField && sortOrder === 'desc') {
            sortField = `-${sortField}`;
        }

        const params: GetPodsParams = {
            ...searchParams,
            sort: sortField as GetPodsParams['sort'],
            page: paginationModel.page + 1,
            per_page: paginationModel.pageSize,
        };
        if (activeSubFilter === 'activeAthlete') {
            params.has_active_sub = true;
        } else if (activeSubFilter === 'activeOrg') {
            params.active_org_sub = true;
        } else if (activeSubFilter === 'partialOrg') {
            params.partial_org_sub = true;
        } else if (activeSubFilter === 'pastDue') {
            params.past_due = true;
        } else if (activeSubFilter === 'pastDueInUse') {
            params.past_due_in_use = true;
        } else if (activeSubFilter === 'comp') {
            params.is_comp = true;
        } else if (activeSubFilter === 'inStock') {
            params.in_stock = true;
        } else if (activeSubFilter === 'inStockDecommissioned') {
            params.in_stock_decommissioned = true;
        }

        await getPodSearchResults(params);
    };

    const handleNewPodParameterChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        const { name, value } = event.target;
        let updatedSimpleId = newPodData.simple_id;

        if (name === 'serial_number' || name === 'club_id') {
            const serialNumber = name === 'serial_number' ? value : newPodData.serial_number;
            const clubId = name === 'club_id' ? value : newPodData.club_id;
            if (serialNumber && clubId) {
                updatedSimpleId = `${serialNumber.slice(-4)}-${clubId}`;
            } else if (serialNumber) {
                updatedSimpleId = `${serialNumber.slice(-4)}-`;
            } else if (clubId) {
                updatedSimpleId = `-${clubId}`;
            } else {
                updatedSimpleId = '';
            }
        }

        setNewPodData({
            ...newPodData,
            [name]: value,
            simple_id: name === 'simple_id' ? value : updatedSimpleId,
        });
    };

    const debouncedSetPodTransferNameFilter = debounce((newInputValue) => {
        setPodTransferNameFilterForCreate(newInputValue);
    }, 300);

    const debouncedSetPodAthleteNameSearchFilter = debounce((newInputValue) => {
        setPodAthleteNameFilterForSearch(newInputValue);
    }, 300);

    // @ts-ignore
    return (
        <Box sx={{ margin: theme.spacing(0, 2) }}>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
            >
                <SnackbarContent message={snackbarMessage} />
            </Snackbar>
            <BulkActionsModal />
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                <h2>Pods</h2>
                <Button
                    component={RouterLink}
                    to="/admin/pods/dashboard"
                    variant="outlined"
                    size="small"
                >
                    Pod dashboard
                </Button>
            </Box>
            <section style={{ marginBottom: '2rem' }}>
                <h4>Create New Pod</h4>
                <ResponsiveForm onSubmit={handleNewPodSubmit} noValidate>
                    <ResponsiveFormItem>
                        <Autocomplete
                            value={
                                allOrganizations.find(
                                    (org) => org.uuid === newPodData.organization_uuid,
                                ) || null
                            }
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason !== 'reset') {
                                    fetchAllOrganizations(newInputValue);
                                }
                            }}
                            onChange={(event, newValue) => {
                                setSelectedOrganization(newValue);
                                setSelectedIndividualForPodCreate(null);
                                setNewPodData({
                                    ...newPodData,
                                    organization_uuid: newValue ? newValue.uuid : '',
                                    individual_uuid: '',
                                });
                            }}
                            getOptionKey={(option) => option.uuid}
                            filterOptions={(options) => options}
                            size={'small'}
                            fullWidth
                            options={allOrganizations}
                            getOptionLabel={(option) =>
                                `${option.name} (${option.subscription_tier?.name})`
                            }
                            renderOption={(props, option) => (
                                <li {...props}>
                                    {`${option.name} (${option.subscription_tier?.name}) - ${option.uuid}`}
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField {...params} label="Select Organization" />
                            )}
                        />
                    </ResponsiveFormItem>
                    <ResponsiveFormItem>
                        <Autocomplete
                            id="individuals-select"
                            options={individualsForPodCreate}
                            getOptionLabel={(option) => option.name}
                            value={selectedIndividualForPodCreate}
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason === 'input') {
                                    debouncedSetPodTransferNameFilter(newInputValue);
                                }
                            }}
                            onChange={(event, newValue) => {
                                setSelectedIndividualForPodCreate(newValue);
                                setNewPodData({
                                    ...newPodData,
                                    individual_uuid: newValue ? newValue.uuid : '',
                                });
                            }}
                            fullWidth
                            size={'small'}
                            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                            renderOption={(props, option) => (
                                <li
                                    {...props}
                                    key={option.uuid}
                                    style={{ display: 'flex', alignItems: 'center' }}
                                >
                                    {option.image_urls.avatar ? (
                                        <img
                                            src={option.image_urls.avatar}
                                            alt={option.name}
                                            style={{
                                                marginRight: 4,
                                                width: 20,
                                                height: 20,
                                            }}
                                        />
                                    ) : (
                                        <div
                                            style={{
                                                marginRight: 4,
                                                width: 20,
                                                height: 20,
                                            }}
                                        ></div>
                                    )}
                                    <span style={{ fontSize: '0.7rem' }}>
                                        {option.name}
                                        &nbsp; ({option.managing_user?.first_name}{' '}
                                        {option.managing_user?.last_name} -{' '}
                                        {option.managing_user?.user_name})
                                    </span>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField {...params} label="Select Individual" />
                            )}
                        />
                    </ResponsiveFormItem>
                    <ResponsiveFormItem>
                        <TextField
                            name="serial_number"
                            value={newPodData.serial_number}
                            onChange={handleNewPodParameterChange}
                            placeholder="Enter Serial Number"
                            size={'small'}
                            fullWidth
                        />
                    </ResponsiveFormItem>
                    <ResponsiveFormItem>
                        <TextField
                            name="club_id"
                            value={newPodData.club_id}
                            onChange={handleNewPodParameterChange}
                            placeholder="Enter Club ID"
                            size={'small'}
                            fullWidth
                        />
                    </ResponsiveFormItem>
                    <ResponsiveFormItem>
                        <TextField
                            name="simple_id"
                            value={newPodData.simple_id}
                            onChange={handleNewPodParameterChange}
                            placeholder="Enter Simple ID"
                            size={'small'}
                            fullWidth
                        />
                    </ResponsiveFormItem>
                    <ResponsiveFormItem>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="decrement_org_pod_subscription"
                                    checked={newPodData.decrement_org_pod_subscription || false}
                                    onChange={(event) =>
                                        setNewPodData({
                                            ...newPodData,
                                            decrement_org_pod_subscription: event.target.checked,
                                        })
                                    }
                                    disabled={!selectedIndividualForPodCreate}
                                    style={{ marginLeft: '8px' }}
                                />
                            }
                            label="Decrement Org Pod Subscription"
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
                        />
                    </ResponsiveFormItem>
                    <ResponsiveFormItem>
                        <Button
                            type="submit"
                            size={'medium'}
                            variant="contained"
                            fullWidth={isNarrow}
                        >
                            Create Pod
                        </Button>
                    </ResponsiveFormItem>
                </ResponsiveForm>
            </section>

            <section style={{ marginBottom: '2rem' }}>
                <h4>Filter Pods</h4>
                <ResponsiveForm onSubmit={handlePodSearch} noValidate>
                    <ResponsiveFormItem>
                        <Autocomplete
                            value={
                                organizations.find(
                                    (org) => org.uuid === searchParams.organization_uuid,
                                ) || null
                            }
                            onChange={(event, newValue) => {
                                setSelectedIndividualForPodSearch(null);
                                setSearchParams({
                                    ...searchParams,
                                    organization_uuid: newValue ? newValue.uuid : '',
                                    athlete_user_uuid: '',
                                });
                            }}
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason !== 'reset') {
                                    fetchOrganizations(newInputValue);
                                }
                            }}
                            getOptionKey={(option) => option.uuid}
                            filterOptions={(options) => options}
                            size={'small'}
                            fullWidth
                            options={organizations}
                            getOptionLabel={(option) =>
                                `${option.name} (${option.subscription_tier?.name})`
                            }
                            renderOption={(props, option) => (
                                <li {...props}>
                                    {`${option.name} (${option.subscription_tier?.name}) - ${option.uuid}`}
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField {...params} label="All Organizations" />
                            )}
                        />
                    </ResponsiveFormItem>
                    <ResponsiveFormItem>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="pod-state-label">Pod State</InputLabel>
                            <Select
                                labelId="pod-state-label"
                                label="Pod State"
                                value={searchParams.pod_state}
                                onChange={(e) => {
                                    const newPodState = e.target.value as PodState;
                                    setSearchParams((prev) => ({
                                        ...prev,
                                        pod_state: newPodState,
                                    }));
                                }}
                                size="small"
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="free">Free</MenuItem>
                                <MenuItem value="transferred">Transferred</MenuItem>
                                <MenuItem value="assigned">Assigned</MenuItem>
                                <MenuItem value="pending_transfer">Pending Transfer</MenuItem>
                            </Select>
                        </FormControl>
                    </ResponsiveFormItem>
                    <ResponsiveFormItem>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="custody-status-label">Custody Status</InputLabel>
                            <Select
                                labelId="custody-status-label"
                                label="Custody Status"
                                value={searchParams.custody_status}
                                onChange={(e) => {
                                    const newCustodyStatus = e.target.value as CustodyStatus;
                                    setSearchParams((prev) => ({
                                        ...prev,
                                        custody_status: newCustodyStatus,
                                    }));
                                }}
                                size="small"
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="in_warehouse">In Warehouse</MenuItem>
                                <MenuItem value="shipped">Shipped</MenuItem>
                                <MenuItem value="return_requested">Return Requested</MenuItem>
                                <MenuItem value="returned">Returned</MenuItem>
                                <MenuItem value="returning">Returning</MenuItem>
                                <MenuItem value="shipped_no_sub">
                                    <Chip
                                        label="Shipped - No Sub"
                                        size="small"
                                        sx={{
                                            backgroundColor: '#d32f2f',
                                            color: 'white',
                                            fontSize: '0.7rem',
                                            height: '20px',
                                        }}
                                    />
                                </MenuItem>
                                <MenuItem value="comp_no_sub">
                                    <Chip
                                        label="Comp - No Sub"
                                        size="small"
                                        sx={{
                                            backgroundColor: '#7b1fa2',
                                            color: 'white',
                                            fontSize: '0.7rem',
                                            height: '20px',
                                        }}
                                    />
                                </MenuItem>
                                <MenuItem value="problem_pods">
                                    <Chip
                                        label="Problem Pods"
                                        size="small"
                                        sx={{
                                            backgroundColor: '#d32f2f',
                                            color: 'white',
                                            fontSize: '0.7rem',
                                            height: '20px',
                                        }}
                                    />
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </ResponsiveFormItem>
                    <ResponsiveFormItem>
                        <Autocomplete
                            id="individuals-select-search"
                            options={individualsForPodSearch}
                            getOptionLabel={(option) => option.name}
                            value={selectedIndividualForPodSearch}
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason === 'input') {
                                    debouncedSetPodAthleteNameSearchFilter(newInputValue);
                                }
                            }}
                            onChange={(event, newValue) => {
                                setSelectedIndividualForPodSearch(newValue);
                                setSearchParams({
                                    ...searchParams,
                                    athlete_user_uuid:
                                        newValue && newValue.managing_user_uuid
                                            ? newValue.managing_user_uuid
                                            : '',
                                });
                            }}
                            fullWidth
                            size={'small'}
                            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                            renderOption={(props, option) => (
                                <li
                                    {...props}
                                    key={option.uuid}
                                    style={{ display: 'flex', alignItems: 'center' }}
                                >
                                    {option.image_urls.avatar ? (
                                        <img
                                            src={option.image_urls.avatar}
                                            alt={option.name}
                                            style={{
                                                marginRight: 4,
                                                width: 20,
                                                height: 20,
                                            }}
                                        />
                                    ) : (
                                        <div
                                            style={{
                                                marginRight: 4,
                                                width: 20,
                                                height: 20,
                                            }}
                                        ></div>
                                    )}
                                    <span style={{ fontSize: '0.7rem' }}>
                                        <strong>{option.name}</strong>
                                        &nbsp; ({option.managing_user?.first_name}{' '}
                                        {option.managing_user?.last_name} -{' '}
                                        {option.managing_user?.user_name}) -{' '}
                                        {option.organization.name}
                                    </span>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField {...params} label="Select Individual" />
                            )}
                        />
                    </ResponsiveFormItem>
                    <ResponsiveFormItem>
                        <TextField
                            name="serial_number"
                            value={searchParams.serial_number}
                            onChange={handleSearchParameterChange}
                            placeholder="Enter Serial Number"
                            size={'small'}
                            fullWidth
                        />
                    </ResponsiveFormItem>
                    <ResponsiveFormItem>
                        <TextField
                            name="club_id"
                            value={searchParams.club_id}
                            onChange={handleSearchParameterChange}
                            placeholder="Enter Club ID"
                            size={'small'}
                            fullWidth
                        />
                    </ResponsiveFormItem>
                    <ResponsiveFormItem>
                        <TextField
                            name="simple_id"
                            value={searchParams.simple_id}
                            onChange={handleSearchParameterChange}
                            placeholder="Enter Simple ID"
                            size={'small'}
                            fullWidth
                        />
                    </ResponsiveFormItem>
                    <ResponsiveFormItem>
                        <Button
                            type="submit"
                            size={'medium'}
                            variant="contained"
                            fullWidth={isNarrow}
                        >
                            Search Pods
                        </Button>
                    </ResponsiveFormItem>
                </ResponsiveForm>
            </section>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Button
                    variant={activeSubFilter === 'activeAthlete' ? 'contained' : 'outlined'}
                    color="success"
                    onClick={() => {
                        setActiveSubFilter((prev) =>
                            prev === 'activeAthlete' ? null : 'activeAthlete',
                        );
                        setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                >
                    Active Athlete Subscriptions
                    <Chip
                        label={activeAthleteSubCount ?? '…'}
                        size="small"
                        sx={{
                            ml: 1,
                            fontWeight: 'bold',
                            minWidth: 32,
                            backgroundColor:
                                activeSubFilter === 'activeAthlete' ? '#fff' : 'success.main',
                            color: activeSubFilter === 'activeAthlete' ? 'success.dark' : '#fff',
                        }}
                    />
                </Button>
                <Button
                    variant={activeSubFilter === 'activeOrg' ? 'contained' : 'outlined'}
                    color="primary"
                    onClick={() => {
                        setActiveSubFilter((prev) => (prev === 'activeOrg' ? null : 'activeOrg'));
                        setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                >
                    Active Org Subscription
                    <Chip
                        label={activeOrgSubCount ?? '…'}
                        size="small"
                        sx={{
                            ml: 1,
                            fontWeight: 'bold',
                            minWidth: 32,
                            backgroundColor:
                                activeSubFilter === 'activeOrg' ? '#fff' : 'primary.main',
                            color: activeSubFilter === 'activeOrg' ? 'primary.dark' : '#fff',
                        }}
                    />
                </Button>
                <Button
                    variant={activeSubFilter === 'partialOrg' ? 'contained' : 'outlined'}
                    color="warning"
                    onClick={() => {
                        setActiveSubFilter((prev) => (prev === 'partialOrg' ? null : 'partialOrg'));
                        setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                >
                    Partial Org Subscription
                    <Chip
                        label={partialOrgSubCount ?? '…'}
                        size="small"
                        sx={{
                            ml: 1,
                            fontWeight: 'bold',
                            minWidth: 32,
                            backgroundColor:
                                activeSubFilter === 'partialOrg' ? '#fff' : 'warning.main',
                            color: activeSubFilter === 'partialOrg' ? 'warning.dark' : '#fff',
                        }}
                    />
                </Button>
                <Button
                    variant={activeSubFilter === 'pastDue' ? 'contained' : 'outlined'}
                    color="error"
                    onClick={() => {
                        setActiveSubFilter((prev) => (prev === 'pastDue' ? null : 'pastDue'));
                        setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                >
                    Past Due - Collections
                    <Chip
                        label={pastDueCount ?? '…'}
                        size="small"
                        sx={{
                            ml: 1,
                            fontWeight: 'bold',
                            minWidth: 32,
                            backgroundColor: activeSubFilter === 'pastDue' ? '#fff' : 'error.main',
                            color: activeSubFilter === 'pastDue' ? 'error.dark' : '#fff',
                        }}
                    />
                </Button>
                <Button
                    variant={activeSubFilter === 'pastDueInUse' ? 'contained' : 'outlined'}
                    color="error"
                    onClick={() => {
                        setActiveSubFilter((prev) =>
                            prev === 'pastDueInUse' ? null : 'pastDueInUse',
                        );
                        setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                >
                    Past Due - IN USE
                    <Chip
                        label={pastDueInUseCount ?? '...'}
                        size="small"
                        sx={{
                            ml: 1,
                            fontWeight: 'bold',
                            minWidth: 32,
                            backgroundColor:
                                activeSubFilter === 'pastDueInUse' ? '#fff' : 'error.main',
                            color: activeSubFilter === 'pastDueInUse' ? 'error.dark' : '#fff',
                        }}
                    />
                </Button>
                <Button
                    variant={activeSubFilter === 'comp' ? 'contained' : 'outlined'}
                    color="info"
                    onClick={() => {
                        setActiveSubFilter((prev) => (prev === 'comp' ? null : 'comp'));
                        setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                >
                    Comp Pods
                    <Chip
                        label={compCount ?? '…'}
                        size="small"
                        sx={{
                            ml: 1,
                            fontWeight: 'bold',
                            minWidth: 32,
                            backgroundColor: activeSubFilter === 'comp' ? '#fff' : 'info.main',
                            color: activeSubFilter === 'comp' ? 'info.dark' : '#fff',
                        }}
                    />
                </Button>
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                    }}
                >
                    <Button
                        variant={activeSubFilter === 'inStock' ? 'contained' : 'outlined'}
                        color="secondary"
                        onClick={() => {
                            setActiveSubFilter((prev) => (prev === 'inStock' ? null : 'inStock'));
                            setPaginationModel((prev) => ({ ...prev, page: 0 }));
                        }}
                    >
                        In Stock
                        <Chip
                            label={inStockCount ?? '…'}
                            size="small"
                            sx={{
                                ml: 1,
                                fontWeight: 'bold',
                                minWidth: 32,
                                backgroundColor:
                                    activeSubFilter === 'inStock' ? '#fff' : 'secondary.main',
                                color: activeSubFilter === 'inStock' ? 'secondary.dark' : '#fff',
                            }}
                        />
                    </Button>
                    {inStockDecommissionedCount != null && inStockDecommissionedCount > 0 && (
                        <Tooltip title="Decommissioned inventory pods (click to show in the table)">
                            <Chip
                                label={inStockDecommissionedCount}
                                size="small"
                                onClick={() => {
                                    setActiveSubFilter((prev) =>
                                        prev === 'inStockDecommissioned'
                                            ? null
                                            : 'inStockDecommissioned',
                                    );
                                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                                }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setActiveSubFilter((prev) =>
                                            prev === 'inStockDecommissioned'
                                                ? null
                                                : 'inStockDecommissioned',
                                        );
                                        setPaginationModel((prev) => ({ ...prev, page: 0 }));
                                    }
                                }}
                                sx={{
                                    height: 22,
                                    minWidth: 28,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    backgroundColor:
                                        activeSubFilter === 'inStockDecommissioned'
                                            ? 'secondary.light'
                                            : 'action.disabledBackground',
                                    color:
                                        activeSubFilter === 'inStockDecommissioned'
                                            ? 'secondary.dark'
                                            : 'text.secondary',
                                    border:
                                        activeSubFilter === 'inStockDecommissioned'
                                            ? (theme) => `2px solid ${theme.palette.secondary.main}`
                                            : '2px solid transparent',
                                    '&:hover': {
                                        backgroundColor:
                                            activeSubFilter === 'inStockDecommissioned'
                                                ? 'secondary.light'
                                                : 'action.hover',
                                    },
                                }}
                            />
                        </Tooltip>
                    )}
                </Box>
            </Box>
            <Box
                sx={{
                    width: '98%',
                    height: 800,
                    overflow: 'hidden',
                }}
            >
                <DataGridPremium<Pod>
                    rows={pods}
                    columns={columns}
                    checkboxSelection={false}
                    sortingMode="server"
                    onSortModelChange={(model) => handlePodSort(model)}
                    getRowId={(row) => row.uuid}
                    style={{
                        height: '100%',
                    }}
                    paginationMode="server"
                    pagination
                    rowCount={totalRows}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[5, 10, 25, 50, 100]}
                    processRowUpdate={handleRowUpdate}
                    disableColumnMenu={true}
                    sx={{
                        '& .MuiDataGrid-cell:focus': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-cell:focus-within': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-cell': {
                            fontSize: '0.7rem',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontSize: '0.7rem',
                        },
                    }}
                />
            </Box>
            <Dialog
                open={openDialog}
                onClose={closeDialogModal}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <Paper style={{ minWidth: '300px' }}>
                    <DialogTitle id="alert-dialog-title">{dialogTitle}</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            {dialogMessage}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeDialogModal} variant="contained" autoFocus>
                            OK
                        </Button>
                    </DialogActions>
                </Paper>
            </Dialog>
        </Box>
    );
};

export default PodList;
